/**
 * Catching Chrome's install offer, which arrives once and does not come back.
 *
 * `beforeinstallprompt` fires a single time per page load, on Chrome's own schedule,
 * and there is no way to ask for it again. The listener used to be attached by an
 * effect inside the install banner, which is rendered by Home, which the app does not
 * render until the manifest fetch resolves — measured at **1,275 ms on a cold load,
 * 770 ms after DOMContentLoaded**. Anything Chrome fired inside that window was gone,
 * and the symptom is silence: no error, no banner, and no way to tell from the app
 * whether the browser had offered and been missed or had never offered at all.
 *
 * So the event is caught in three places, earliest first: an inline script in the
 * document head (see index.html), this module at import time, and a listener here for
 * anything still to come. This module reads whatever the head script already caught.
 *
 * The second half of the fix is not taking the event over when we are not going to use
 * it. `preventDefault()` suppresses Chrome's own install UI; combined with a permanent
 * dismissal flag that outlives an uninstall, a user could end up in a state where
 * neither Chrome nor the app would ever offer again. See `installDismissed` in
 * settings.ts.
 */

import { signal } from '@preact/signals';
import { installDismissed, settings, updateSettings } from './settings';

/** The slice of BeforeInstallPromptEvent we use; it is not in lib.dom. */
export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** What the inline head script leaves behind for us. Kept deliberately dumb. */
export interface EarlyInstallCapture {
  event: InstallPromptEvent | null;
  /** Chrome fired the event at least once this page load. */
  fired: boolean;
  /** We let Chrome show its own offer rather than taking the event over. */
  native: boolean;
  installed: boolean;
}

declare global {
  interface Window { __brahmInstall?: EarlyInstallCapture }
}

export const installEvent = signal<InstallPromptEvent | null>(null);
/** Chrome offered at some point this page load, whoever ended up showing it. */
export const promptFired = signal(false);
/** We deliberately left the offer to Chrome, so the app should not claim it can install. */
export const nativeOffer = signal(false);
export const standalone = signal(isStandalone());

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS predates display-mode and uses a non-standard flag on navigator.
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return window.matchMedia?.('(display-mode: standalone)').matches || iosStandalone;
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; the touch-point count is what separates them.
  return /iphone|ipad|ipod/i.test(ua)
    || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

export type InstallState =
  | { kind: 'installed' }
  | { kind: 'prompt' }
  /** Chrome is offering this itself; we must not draw a button that duplicates it. */
  | { kind: 'native' }
  | { kind: 'ios' }
  | { kind: 'waiting' };

export function installState(now = Date.now()): InstallState {
  if (standalone.value) return { kind: 'installed' };
  if (installEvent.value && !nativeOffer.value) return { kind: 'prompt' };
  if (nativeOffer.value) return { kind: 'native' };
  if (isIos()) return { kind: 'ios' };
  void now;
  return { kind: 'waiting' };
}

/** Whether the home-screen banner should be on screen right now. */
export function offerVisible(now = Date.now()): boolean {
  const state = installState(now);
  if (state.kind === 'installed' || state.kind === 'native') return false;
  if (state.kind === 'waiting' && !isIos()) return false;
  return !installDismissed(settings.value, now);
}

export function dismissOffer(now = Date.now()): void {
  updateSettings({ installDismissedAt: now });
}

/**
 * Ask Chrome to install. Returns false if it declined to, for any reason.
 *
 * `prompt()` can reject when the event has already been consumed or was never taken
 * over, and a rejection here must not surface as a broken button — the honest fallback
 * is to go back to reporting that the browser has not offered.
 */
export async function promptToInstall(): Promise<boolean> {
  const e = installEvent.value;
  if (!e) return false;
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    installEvent.value = null;          // single-use whatever the answer
    return outcome === 'accepted';
  } catch {
    installEvent.value = null;
    return false;
  }
}

let watching = false;

/**
 * Start watching. Safe to call more than once; call it as early as main can.
 */
export function startInstallWatch(): () => void {
  const early = typeof window !== 'undefined' ? window.__brahmInstall : undefined;
  if (early) {
    if (early.event) installEvent.value = early.event;
    if (early.fired) promptFired.value = true;
    if (early.native) nativeOffer.value = true;
    if (early.installed) standalone.value = true;
  }
  if (watching || typeof window === 'undefined') return () => {};
  watching = true;

  const onPrompt = (e: Event) => {
    promptFired.value = true;
    installEvent.value = e as InstallPromptEvent;
    // The head script has already decided whether to take the event over. If it is not
    // loaded (a test page, a stripped build) decide the same way here.
    if (!early && !installDismissed(settings.value)) e.preventDefault();
    else if (!early) nativeOffer.value = true;
  };
  const onInstalled = () => {
    updateSettings({ installedAt: Date.now() });
    standalone.value = true;
    installEvent.value = null;
  };
  window.addEventListener('beforeinstallprompt', onPrompt);
  window.addEventListener('appinstalled', onInstalled);

  // Someone can install, then reopen the tab; display-mode flips without an event.
  const mq = window.matchMedia?.('(display-mode: standalone)');
  const onMode = () => { standalone.value = isStandalone(); };
  mq?.addEventListener?.('change', onMode);

  return () => {
    window.removeEventListener('beforeinstallprompt', onPrompt);
    window.removeEventListener('appinstalled', onInstalled);
    mq?.removeEventListener?.('change', onMode);
    watching = false;
  };
}

/**
 * One sentence saying why there is no Install button, for Settings.
 *
 * This exists because "it does not show the pop-up" was, for a while, unanswerable from
 * inside the app: four different states render as nothing at all.
 */
export function installExplanation(s = installState(), now = Date.now()): string {
  switch (s.kind) {
    case 'installed':
      return 'Installed to your home screen.';
    case 'prompt':
      return 'Ready to install.';
    case 'native':
      return 'Your browser is offering to install this itself — look for the install '
        + 'icon in the address bar, or the bar along the bottom.';
    case 'ios':
      return 'To install: tap Share, then Add to Home Screen.';
    default:
      break;
  }
  if (installDismissed(settings.value, now)) {
    return 'You dismissed the install offer. It comes back on its own, or use the '
      + 'button above.';
  }
  if (promptFired.value) {
    return 'Your browser offered to install this and the offer has been used up. '
      + 'Reload the page to get it back.';
  }
  return 'Your browser has not offered to install this yet. Chrome waits until you '
    + 'have visited a couple of times before it will.';
}
